import { useEffect, useId, useRef, useState } from "react";
import * as Popover from "@radix-ui/react-popover";
import { ChevronDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { FOCUS_RING, useFieldContext } from "@/components/ui-kit";

/**
 * Combobox de filtro com busca digitada.
 *
 * Dois modos, escolhidos por quem usa:
 * - lista local: passe `options` e nada mais — o filtro é feito aqui mesmo.
 * - busca no servidor: passe também `query`/`onQueryChange` (normalmente o
 *   search/setSearch de um usePaginatedList) — aí não filtramos de novo no
 *   cliente, senão a lista já paginada seria recortada duas vezes.
 *
 * `selectedLabel` existe porque o item escolhido pode não estar na página
 * atual de resultados: sem ele o campo ficaria em branco com o filtro ativo.
 *
 * `emptyLabel` separa o texto de "limpar seleção" do placeholder. Em filtro os
 * dois coincidem ("Todos fornecedores"), mas em formulário o placeholder é uma
 * instrução ("Buscar fornecedor...") que não faz sentido como opção da lista.
 *
 * Acessibilidade (padrão combobox da ARIA 1.2): o foco não sai do input — a
 * opção em destaque é anunciada por `aria-activedescendant`, e `role="option"`
 * fica no `<li>`, filho direto do `role="listbox"`. Antes o "limpar" era um
 * `<svg role="button">` dentro do `<button>` de abrir: botão aninhado é HTML
 * inválido e ficava fora da ordem de tabulação.
 *
 * A lista sai em PORTAL (Popover do Radix). Posicionada no fluxo, ela entrava na
 * área rolável do que estivesse em volta: dentro de um diálogo, abrir a busca
 * fazia nascer uma SEGUNDA barra de rolagem coladinha na da lista — duas barras
 * lado a lado para o mesmo gesto —, e dentro de uma tabela com rolagem
 * horizontal ela era cortada pela borda. No portal a lista flutua sobre a tela,
 * o Radix cuida de virar para cima quando não há espaço embaixo, e o diálogo
 * volta a rolar só quando o CONTEÚDO dele é grande.
 */
export default function Autocomplete({
  value,
  onChange,
  options = [],
  query,
  onQueryChange,
  selectedLabel,
  placeholder = "Todos",
  emptyLabel,
  ariaLabel,
  testId,
  width = "w-52",
}) {
  const serverSide = typeof onQueryChange === "function";
  const [open, setOpen] = useState(false);
  const [local, setLocal] = useState("");
  const [active, setActive] = useState(0);
  const boxRef = useRef(null);
  const inputRef = useRef(null);
  const uid = useId();
  const listId = `${uid}-list`;
  const optionId = (i) => `${uid}-opt-${i}`;

  /* Dentro de um `Field`, quem nomeia o campo é o <label> visível: o controle
     recebe o id do Field (tanto `<input>` quanto `<button>` são "labelable") e
     dispensa o aria-label, que só duplicaria o anúncio. Isso é o que permite
     `<Field label="Cliente"><CustomerPicker /></Field>` funcionar de fato —
     antes o <label> ficava solto ao lado de um componente customizado. */
  const field = useFieldContext();
  const controlId = field?.id;
  const fallbackLabel = field ? undefined : (ariaLabel || placeholder);

  const text = serverSide ? (query ?? "") : local;
  const setText = serverSide ? onQueryChange : setLocal;

  const list = serverSide
    ? options
    : options.filter((o) => o.label.toLowerCase().includes(local.trim().toLowerCase()));

  const current = options.find((o) => o.value === value);
  // `displayLabel` separa o texto do campo fechado do texto da lista. Serve à
  // opção de texto livre: na lista ela é uma ação ("Usar X (fora do catálogo)"),
  // mas depois de escolhida o campo tem de mostrar só o X.
  const label = current?.displayLabel || current?.label || selectedLabel || "";

  useEffect(() => { if (open) inputRef.current?.focus(); }, [open]);
  useEffect(() => { setActive(0); }, [text]);

  const pick = (opt) => {
    onChange(opt ? opt.value : "", opt ? opt.label : "");
    setOpen(false);
    if (!serverSide) setLocal("");
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => Math.min(i + 1, list.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); if (list[active]) pick(list[active]); }
    else if (e.key === "Escape") { e.preventDefault(); setOpen(false); }
    else if (e.key === "Tab") { setOpen(false); }
  };

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <div ref={boxRef} className={cn("relative", width)} data-testid={testId}>
        <Popover.Anchor asChild>
          {open ? (
            <input
              ref={inputRef}
              id={controlId}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={placeholder}
              aria-label={fallbackLabel}
              aria-describedby={field?.describedBy}
              role="combobox"
              aria-expanded="true"
              aria-controls={listId}
              aria-autocomplete="list"
              aria-activedescendant={list[active] ? optionId(active) : undefined}
              data-testid={testId ? `${testId}-input` : undefined}
              data-ring=""
              className={cn("w-full border border-brand-500 rounded-lg px-2.5 h-10 text-sm bg-white", FOCUS_RING)}
            />
          ) : (
            // O "limpar" é irmão do botão de abrir, não filho: posicionado por cima,
            // com área de toque própria e alcançável por Tab.
            <div className="relative">
              <button
                type="button"
                id={controlId}
                onClick={() => setOpen(true)}
                aria-label={fallbackLabel}
                aria-describedby={field?.describedBy}
                aria-expanded="false"
                data-testid={testId ? `${testId}-toggle` : undefined}
                data-ring=""
                className={cn(
                  "w-full flex items-center justify-between gap-1 border border-slate-200 rounded-lg",
                  "pl-2.5 h-10 text-sm bg-white text-left hover:border-slate-300 transition-colors",
                  value ? "pr-9" : "pr-2.5", FOCUS_RING,
                )}
              >
                <span className={cn("truncate", value ? "text-slate-800" : "text-slate-600")}>
                  {value ? label : placeholder}
                </span>
                {!value && <ChevronDown size={14} className="shrink-0 text-slate-400" aria-hidden />}
              </button>
              {value && (
                <button
                  type="button"
                  onClick={() => pick(null)}
                  aria-label="Limpar seleção"
                  data-testid={testId ? `${testId}-clear` : undefined}
                  data-ring=""
                  className={cn(
                    "absolute right-1 top-1/2 -translate-y-1/2 p-1.5 rounded-md",
                    "text-slate-400 hover:text-red-500 hover:bg-slate-100 transition-colors", FOCUS_RING,
                  )}
                >
                  <X size={14} />
                </button>
              )}
            </div>
          )}
        </Popover.Anchor>

        <Popover.Portal>
          <Popover.Content
            side="bottom"
            align="start"
            sideOffset={4}
            collisionPadding={8}
            // O foco NÃO vai para a lista: quem manda continua sendo o input, que
            // anuncia a opção em destaque por aria-activedescendant.
            onOpenAutoFocus={(e) => e.preventDefault()}
            onCloseAutoFocus={(e) => e.preventDefault()}
            // O campo é a âncora, não o gatilho do Radix: sem isto, clicar no
            // próprio campo para posicionar o cursor contaria como "clique fora"
            // e fecharia a lista embaixo do dedo.
            onInteractOutside={(e) => {
              const alvo = e.detail?.originalEvent?.target;
              if (alvo && boxRef.current?.contains(alvo)) e.preventDefault();
            }}
            // Mesma largura do campo, e no máximo o que couber na tela.
            className="z-50 w-[var(--radix-popover-trigger-width)]"
          >
            <ul
              id={listId}
              role="listbox"
              aria-label={ariaLabel || placeholder}
              className="max-h-64 overflow-y-auto bg-white border border-slate-200 rounded-xl shadow-lg py-1 scrollbar-thin"
            >
              {/* Opção de limpar: participa da lista, então também é role=option. */}
              <li
                role="option"
                aria-selected={!value}
                onMouseDown={(e) => { e.preventDefault(); pick(null); }}
                className="px-3 py-2 text-sm text-slate-500 hover:bg-slate-50 cursor-pointer"
              >
                {emptyLabel ?? placeholder}
              </li>
              {list.map((o, i) => (
                <li
                  key={o.value}
                  id={optionId(i)}
                  role="option"
                  aria-selected={o.value === value}
                  onMouseDown={(e) => { e.preventDefault(); pick(o); }}
                  onMouseEnter={() => setActive(i)}
                  className={cn(
                    "px-3 py-2 text-sm truncate cursor-pointer",
                    i === active ? "bg-brand-50 text-brand-700" : "text-slate-700 hover:bg-slate-50",
                  )}
                >
                  {o.label}
                </li>
              ))}
              {list.length === 0 && (
                <li role="option" aria-selected={false} aria-disabled className="px-3 py-2 text-sm text-slate-400">
                  Nada encontrado
                </li>
              )}
            </ul>
          </Popover.Content>
        </Popover.Portal>
      </div>
    </Popover.Root>
  );
}
